// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Coming soon previews  (route /coming-soon/:slug — one page per preview in data/comingSoon.ts)
// Renders a mockup screen that isn't built yet: a prominent "Coming soon" banner, then the mockup's structure with EVERY control
// disabled (aria-disabled, no handlers) so nothing can be used or ordered. All figures are sample content from the design and say so.
// Marked noindex and left out of the sitemap/search. When the real screen exists, set the entry's `live` (a link to it is shown) or
// replace the route. Unknown slug → the 404 page.
// Neighbours: data/comingSoon, pages/ComingSoonIndex, pages/NotFound.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Hourglass } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { previewBySlug, type PreviewBlock } from "../data/comingSoon";
import useDocumentTitle from "../hooks/useDocumentTitle";
import NotFound from "./NotFound";

const card = "rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10";
const soon = "cursor-not-allowed opacity-60";

function Block({ block, index }: { block: PreviewBlock; index: number }) {
  const h = (text: string, aside?: string) => (
    <div className="flex items-center justify-between gap-2">
      <h2 className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{text}</h2>
      {aside && <span className="shrink-0 text-xs text-ocean-800/80 dark:text-foam-100/75">{aside}</span>}
    </div>
  );
  switch (block.kind) {
    case "cards":
      return (
        <section aria-label={block.heading} className="flex flex-col gap-2">
          {h(block.heading, block.aside)}
          <ul className="flex flex-col gap-2">
            {block.items.map((item) => (
              <li key={item.title} className={`${card} ${soon}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-bold text-ocean-950 dark:text-foam-50">{item.title}</p>
                  {item.tag && <span className="shrink-0 rounded-full bg-teal-100 px-2 py-0.5 text-[11px] font-bold text-teal-700">{item.tag}</span>}
                </div>
                <p className="mt-1 text-xs text-ocean-800/90 dark:text-foam-100/85">{item.body}</p>
                {item.price && (
                  <p className="mt-2 flex items-baseline gap-2">
                    <span className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{item.price}</span>
                    {item.was && <span className="text-xs text-ocean-800/70 line-through dark:text-foam-100/65">{item.was}</span>}
                    <span className="text-[11px] font-bold text-ocean-800/80 dark:text-foam-100/75">sample</span>
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      );
    case "chips":
      return (
        <section aria-label={block.heading} className="flex flex-col gap-2">
          {h(block.heading)}
          <ul className="flex flex-wrap gap-2">
            {block.items.map((chip) => (
              <li key={chip}><button type="button" disabled className={`min-h-11 rounded-full bg-ocean-700/10 px-4 text-sm font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50 ${soon}`}>{chip}</button></li>
            ))}
          </ul>
        </section>
      );
    case "steps":
      return (
        <section aria-label={block.heading} className="flex flex-col gap-2">
          {h(block.heading)}
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {block.items.map((step) => (
              <li key={step.label} className={`${card} ${soon} py-3 text-center`}>
                <p className="text-sm font-bold text-ocean-950 dark:text-foam-50">{step.label}</p>
                <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{step.note}</p>
              </li>
            ))}
          </ul>
        </section>
      );
    case "toggles":
      return (
        <section aria-label={block.heading} className="flex flex-col gap-2">
          {h(block.heading)}
          <ul className={`${card} divide-y divide-ocean-900/10 dark:divide-white/10`}>
            {block.items.map((toggle, i) => (
              <li key={toggle.title} className="flex items-start justify-between gap-3 py-3">
                <div>
                  <p id={`t${index}-${i}`} className="text-sm font-bold text-ocean-950 dark:text-foam-50">{toggle.title}</p>
                  <p className="mt-0.5 text-xs text-ocean-800/85 dark:text-foam-100/80">{toggle.body}</p>
                </div>
                <button type="button" role="switch" aria-checked={false} aria-disabled="true" disabled aria-labelledby={`t${index}-${i}`} className={`mt-1 h-7 w-12 shrink-0 rounded-full bg-ocean-700/25 dark:bg-white/25 ${soon}`} />
              </li>
            ))}
          </ul>
        </section>
      );
    case "faq":
      return (
        <section aria-label={block.heading} className="flex flex-col gap-2">
          {h(block.heading)}
          <ul className="flex flex-col gap-2">
            {block.items.map((faq) => (
              <li key={faq.q} className={card}>
                <p className="text-sm font-bold text-ocean-950 dark:text-foam-50">{faq.q}</p>
                <p className="mt-1 text-xs text-ocean-800/90 dark:text-foam-100/85">{faq.a}</p>
              </li>
            ))}
          </ul>
        </section>
      );
    case "stats":
      return (
        <ul className="grid grid-cols-3 gap-2" aria-label="Sample figures">
          {block.items.map((stat) => (
            <li key={stat.label} className={`${card} p-3 text-center`}>
              <p className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{stat.value}</p>
              <p className="text-[11px] text-ocean-800/80 dark:text-foam-100/75">{stat.label}</p>
            </li>
          ))}
        </ul>
      );
    case "note":
      return (
        <section className="rounded-2xl bg-teal-100/50 p-4 dark:bg-teal-700/10">
          {block.heading && <h2 className="text-sm font-bold text-ocean-950 dark:text-foam-50">{block.heading}</h2>}
          <p className="mt-0.5 text-sm text-ocean-800/90 dark:text-foam-100/85">{block.body}</p>
        </section>
      );
    case "cta":
      return <button type="button" disabled aria-disabled="true" className={`min-h-12 rounded-full bg-ocean-800 px-6 font-bold text-white ${soon}`}>{block.label} — coming soon</button>;
  }
}

export default function ComingSoon() {
  const { slug } = useParams();
  const preview = previewBySlug(slug);
  useDocumentTitle(preview ? `${preview.title} (coming soon)` : "Not found");

  // A preview is not a real page: keep it out of search engines.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  if (!preview) return <NotFound />;

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to={preview.parent.to} aria-label={`Back to ${preview.parent.label}`} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold leading-tight text-ocean-950 dark:text-foam-50">{preview.title}</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Part of {preview.parent.label}</p>
            </div>
          </header>

          <div role="note" className="flex items-start gap-3 rounded-2xl bg-coral-500/10 p-4 ring-1 ring-coral-500/30">
            <Hourglass className="mt-0.5 h-5 w-5 shrink-0 text-coral-600 dark:text-coral-300" aria-hidden="true" />
            <div>
              <p className="font-display text-base font-bold text-ocean-950 dark:text-foam-50">Coming soon</p>
              <p className="text-sm text-ocean-800/90 dark:text-foam-100/85">This is a preview of something we&apos;re still building. Nothing here can be used or ordered yet, and any prices, percentages, times and promises shown are samples that may change.</p>
              {preview.live && <Link to={preview.live} className="mt-2 inline-flex min-h-11 items-center font-bold underline">It&apos;s ready — open it</Link>}
            </div>
          </div>

          {preview.hero && (
            <section className="rounded-2xl bg-ocean-900 p-4 text-foam-50 shadow-md">
              <p className="text-[11px] font-bold uppercase tracking-wider text-teal-300">{preview.hero.eyebrow}</p>
              <h2 className="mt-1 font-display text-xl font-bold leading-snug">{preview.hero.title}</h2>
              <p className="mt-1 text-sm text-foam-100/90">{preview.hero.body}</p>
            </section>
          )}

          {preview.blocks.map((block, index) => <Block key={index} block={block} index={index} />)}

          <Link to={preview.parent.to} className="inline-flex min-h-12 items-center justify-center rounded-full bg-ocean-700/10 px-6 font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">Back to {preview.parent.label}</Link>
        </div>
      </div>
    </PageTransition>
  );
}
